import DPO from 'dpo8core';
import React, { useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { stateEditor } from '@app/actions/customizeActions';
import { inject } from '@app/providers/QAU.ContextProvider';
import { isIOSChromeBrowser } from '@app/utils/device';
import { getEditorConfig } from '@app/utils/editor';

import './WorkingArea.scss';

const WorkingArea = ({ flow, editorZoom, editorExists, stateEditor, sideToolPanelExpanded, isMobileView }) => {
  const prevWorkingAreaScrollRef = useRef();
  const adjustedZoomValueRef = useRef();
  const workingAreaHeightRef = useRef();
  const workingAreaWidthRef = useRef();
  const prevZoomValueRef = useRef();
  const workingAreaElRef = useRef();

  const createEditor = useCallback(() => {
    return new DPO.render.Editor(workingAreaElRef.current, null, getEditorConfig(isMobileView));
  }, [isMobileView]);

  useEffect(() => {
    !editorExists && stateEditor(createEditor());
  }, [createEditor, editorExists, stateEditor]);

  useEffect(() => {
    if (!flow.editor) return;
    const { current: { classList: workingAreaClasses, } } = workingAreaElRef;
    const { project: { masterPanels: [masterPanel] }, editor } = flow;
    const { viewPortCfg: { areaBox: { width, height } } } = editor;
    const panelWidth = DPO.twipsToPoints(masterPanel.width * editorZoom);
    const panelHeight = DPO.twipsToPoints(masterPanel.height * editorZoom);
    const workingAreaWidth = DPO.pixelsToPoints(width);
    const workingAreaHeight = DPO.pixelsToPoints(height);
    const isSizeExceeded = panelHeight > workingAreaHeight || panelWidth > workingAreaWidth;

    workingAreaClasses[isSizeExceeded ? 'add' : 'remove']('scrollbars');
  }, [editorZoom]);

  const adjustEditorSize = useCallback(
    (editor, sideToolPanelExpanded) => {
      const { current: workingAreaEl } = workingAreaElRef;
      const { workingAreaScroll } = editor;

      const shrinkClassName = 'shrinked';
      const shrinkDirection = isMobileView ? 'top' : 'left';
      const workingAreaScrollValue = { [shrinkDirection]: workingAreaScroll[shrinkDirection] };

      if (sideToolPanelExpanded && !prevWorkingAreaScrollRef.current) {
        prevWorkingAreaScrollRef.current = workingAreaScrollValue;
      }

      const { current: prevWorkingAreaScroll } = prevWorkingAreaScrollRef;

      if (sideToolPanelExpanded) {
        prevWorkingAreaScrollRef.current = workingAreaScrollValue;
        workingAreaEl.classList.add(shrinkClassName);
        editor.onResize();
      } else {
        workingAreaEl.classList.remove(shrinkClassName);
        editor.updateWorkingAreaScroll(prevWorkingAreaScroll);
        prevWorkingAreaScrollRef.current = null;
      }

      editor.scrollToCenter();
    },
    [isMobileView],
  );

  const adjustWorkingAreaSize = () => {
    const rect = workingAreaElRef.current.getBoundingClientRect();
    let { width, left, height, top } = rect;

    if (isIOSChromeBrowser()) {
      height -= 5;
    }

    if (!isMobileView && !workingAreaWidthRef.current) {
      workingAreaWidthRef.current = width - left / 2.5;
    }

    if (isMobileView && !workingAreaHeightRef.current) {
      workingAreaHeightRef.current = height - top * 2.5;
    }
  };

  const getZoomFactors = isMobile => {
    return {
      decrease: isMobile ? 0.75 : 0.9, // decreases zoom by 10% (or 25% for mobile mode) if needed
      increase: isMobile ? 1.3333 : 1.1111, // increases zoom by 10% (or 25% for mobile mode) if needed
    };
  }

  const adjustEditorZoom = useCallback(
    (editor, sideToolPanelExpanded) => {
      const { canvas, zoom: currentZoom } = editor;

      if (sideToolPanelExpanded) adjustWorkingAreaSize();

      const { current: workingAreaHeight } = workingAreaHeightRef;
      const { current: workingAreaWidth } = workingAreaWidthRef;
      const { current: adjustedZoom } = adjustedZoomValueRef;
      const { current: prevZoom } = prevZoomValueRef;

      const { value: canvasAreaWidth } = canvas.node.width.baseVal;
      const { value: canvasAreaHeight } = canvas.node.height.baseVal;

      const canvasOverlapped = isMobileView ? canvasAreaHeight >= workingAreaHeight : canvasAreaWidth >= workingAreaWidth;

      const zoomFactors = getZoomFactors(isMobileView);
      let newZoom;

      if (sideToolPanelExpanded) {
        newZoom = canvasOverlapped ? currentZoom * zoomFactors.decrease : currentZoom;
        newZoom = Number(newZoom.toFixed(2));
        prevZoomValueRef.current = currentZoom;
        adjustedZoomValueRef.current = newZoom;
      } else {
        newZoom = canvasOverlapped && currentZoom > prevZoom ? currentZoom * zoomFactors.increase : currentZoom;
        newZoom = Number((adjustedZoom !== currentZoom ? newZoom : prevZoom).toFixed(2));
        adjustedZoomValueRef.current = null;
        prevZoomValueRef.current = null;
      }

      newZoom !== currentZoom && editor.setZoomLevel(newZoom);
    },
    [isMobileView, prevZoomValueRef],
  );

  const adjustEditor = useCallback(
    sideToolPanelExpanded => {
      const { editor } = flow;
      const { masterPanel, panelCfg, zoom } = editor || {};

      if ((!masterPanel && !zoom) || !panelCfg) return;

      const args = [editor, sideToolPanelExpanded];

      if (sideToolPanelExpanded) {
        adjustEditorSize(...args);
        adjustEditorZoom(...args);
      } else {
        adjustEditorZoom(...args);
        adjustEditorSize(...args);
      }
    },
    [flow, adjustEditorSize, adjustEditorZoom],
  );

  useEffect(() => {
    adjustEditor(sideToolPanelExpanded);
  }, [sideToolPanelExpanded, adjustEditor]);

  return <div ref={workingAreaElRef} id="qau-working-area" />;
};

const mapStateToProps = ({
  customize: {
    customizeData: { editor, editorZoom, toolPanelOverlayLayout },
  },
}) => ({
  editorExists: !!editor,
  editorZoom,
  sideToolPanelExpanded: !!toolPanelOverlayLayout,
});

const mapDispatchToProps = {
  stateEditor,
};

WorkingArea.propTypes = {
  flow: PropTypes.object.isRequired,
  editorZoom: PropTypes.number.isRequired,
  stateEditor: PropTypes.func.isRequired,
  editorExists: PropTypes.bool.isRequired,
  isMobileView: PropTypes.bool.isRequired,
  sideToolPanelExpanded: PropTypes.bool.isRequired,
};

export default inject('flow')(connect(mapStateToProps, mapDispatchToProps)(WorkingArea));
